import { useEffect, useState, type FormEvent } from "react";
import { Boxes, ClipboardList, Loader2, X } from "lucide-react";

import { useCreatePanneau, useUpdatePanneau } from "../api/queries";
import type { Panneau, PanneauKind } from "../api/types";

interface Props {
  open: boolean;
  onClose: () => void;
  /** When provided, the modal switches to edit mode — pre-fills the
   *  fields with this panneau's current name + kind + config and PATCHes
   *  on submit. When absent (create mode) it POSTs a new panneau. */
  panneau?: Panneau | null;
  /** Required for invalidating the right panneaux query in edit mode. */
  conv?: string;
}

interface KindOption {
  kind: PanneauKind;
  label: string;
  icon: typeof ClipboardList;
  helper: string;
  /** Whether this kind needs a `client` field. */
  needsClient: boolean;
  /** Whether this kind takes an optional canonical_ids filter. */
  needsCanonicalIds: boolean;
  disabled?: boolean;
}

const KINDS: KindOption[] = [
  {
    kind: "commande_sortante",
    label: "Commande sortante",
    icon: ClipboardList,
    helper: "Suit les propositions de lignes pour un client donné.",
    needsClient: true,
    needsCanonicalIds: false,
  },
  {
    kind: "inventaire",
    label: "Inventaire",
    icon: Boxes,
    helper: "Suit le stock courant pour un ensemble de produits.",
    needsClient: false,
    needsCanonicalIds: true,
  },
  // Rapport is intentionally not in the picker yet — it's not
  // implemented and a disabled tile just creates "what's that for?"
  // confusion. We'll add it back when the kind actually does
  // something. The audit flagged this; PRD §B_G_F still planned.
];

/**
 * Modal triggered by the "Nouveau panneau" header button. Same logic
 * as EmptyState's two starter buttons but generic — pick a kind, give
 * it a name, optionally pin a `client` (commande) or canonical_ids
 * filter (inventaire).
 *
 * Rationale: per the PRD model, panneaux are saved filters/views over
 * the proposals + commande_lines + movements the agent generates.
 * Creating a panneau is purely a frontend action; the agent's tool
 * surface is unchanged. Don't-make-me-think: one button, one modal,
 * minimum required fields, sensible defaults.
 */
export function NewPanneauModal({ open, onClose, panneau, conv }: Props) {
  const isEdit = !!panneau;
  const [kind, setKind] = useState<PanneauKind>("commande_sortante");
  const [name, setName] = useState("");
  const [client, setClient] = useState("");
  const [canonicalIds, setCanonicalIds] = useState("");
  const create = useCreatePanneau();
  // useUpdatePanneau requires non-empty IDs at the hook call site, so we
  // pass safe placeholders in create-mode (the hook is never invoked
  // unless we're editing).
  const update = useUpdatePanneau(panneau?.id ?? "x", conv ?? "x");

  // Pre-fill on open. In edit mode we copy the panneau's current name +
  // kind + config; in create mode everything resets to defaults.
  useEffect(() => {
    if (!open) return;
    if (panneau) {
      setKind(panneau.kind);
      setName(panneau.name);
      const c = panneau.config?.client;
      setClient(typeof c === "string" ? c : "");
      const ids = panneau.config?.canonical_ids;
      setCanonicalIds(Array.isArray(ids) ? ids.join(", ") : "");
    } else {
      setKind("commande_sortante");
      setName("");
      setClient("");
      setCanonicalIds("");
    }
  }, [open, panneau]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const cfg = KINDS.find((k) => k.kind === kind)!;

  // Live default name suggestion — only when the user hasn't typed
  // their own. Krug rule: never show an empty required field where
  // we can guess.
  const suggestedName =
    kind === "commande_sortante" && client.trim()
      ? client.trim()
      : kind === "inventaire"
      ? "Inventaire"
      : kind === "rapport"
      ? "Rapport"
      : "";
  const effectiveName = name.trim() || suggestedName;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!effectiveName) return;

    const config: Record<string, unknown> = {};
    if (cfg.needsClient && client.trim()) {
      config.client = client.trim();
    }
    if (cfg.needsCanonicalIds && canonicalIds.trim()) {
      config.canonical_ids = canonicalIds
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }

    if (isEdit && panneau) {
      // PATCH: kind isn't editable post-creation (changing kind would
      // mean the panneau filter looks at totally different proposal
      // types — easier to delete + recreate), so we only patch name +
      // config.
      await update.mutateAsync({ name: effectiveName, config });
    } else {
      await create.mutateAsync({ name: effectiveName, kind, config });
    }
    onClose();
  };

  const pending = isEdit ? update.isPending : create.isPending;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/30 dark:bg-white/30 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative w-full max-w-md bg-white dark:bg-surface-dark rounded-2xl shadow-xl ring-1 ring-black/10 dark:ring-white/10">
        <header className="flex items-center justify-between px-5 py-4 border-b border-black/10 dark:border-white/10">
          <h2 className="text-base font-semibold">
            {isEdit ? "Modifier le panneau" : "Nouveau panneau"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-full hover:bg-canvas dark:hover:bg-canvas-dark text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark flex items-center justify-center"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <form onSubmit={submit} className="p-5 space-y-4">
          {/* Kind picker — disabled in edit mode (changing kind mid-life
              would orphan the panneau's existing filter; delete-and-
              recreate is the explicit path). */}
          <div className="space-y-2">
            <label className="text-xs text-muted dark:text-muted-dark block">
              Type de panneau
              {isEdit && (
                <span className="ml-1 normal-case text-muted/80 dark:text-muted-dark/80">
                  (non modifiable après création)
                </span>
              )}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {KINDS.map((k) => {
                const Icon = k.icon;
                const active = kind === k.kind;
                const locked = isEdit;
                return (
                  <button
                    key={k.kind}
                    type="button"
                    disabled={k.disabled || locked}
                    onClick={() => !locked && setKind(k.kind)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-xs transition ${
                      active
                        ? "bg-ink text-white border-ink dark:border-white"
                        : "bg-white dark:bg-surface-dark border-black/10 dark:border-white/10 hover:border-ink/40 dark:hover:border-white/40 text-ink dark:text-ink-dark"
                    } ${k.disabled || locked ? "opacity-40 cursor-not-allowed" : ""}`}
                    title={k.helper}
                  >
                    <Icon className="h-4 w-4" />
                    {k.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted dark:text-muted-dark">{cfg.helper}</p>
          </div>

          {/* Conditional fields */}
          {cfg.needsClient && (
            <div>
              <label className="text-xs text-muted dark:text-muted-dark block mb-1">
                Client (filtre) <span className="text-chip-red">*</span>
              </label>
              <input
                type="text"
                required
                value={client}
                onChange={(e) => setClient(e.target.value)}
                placeholder="Jojo & Co"
                className="bun-textbox w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-ink/30 dark:focus:ring-white/30"
              />
              <p className="text-xs text-muted dark:text-muted-dark mt-1">
                Le panneau affichera les propositions où{" "}
                <code>client = {client.trim() || "…"}</code>.
              </p>
            </div>
          )}

          {cfg.needsCanonicalIds && (
            <div>
              <label className="text-xs text-muted dark:text-muted-dark block mb-1">
                Produits (optionnel, séparés par des virgules)
              </label>
              <input
                type="text"
                value={canonicalIds}
                onChange={(e) => setCanonicalIds(e.target.value)}
                placeholder="cafe_chelchele, cafe_gatare"
                className="bun-textbox w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-ink/30 dark:focus:ring-white/30"
              />
              <p className="text-xs text-muted dark:text-muted-dark mt-1">
                Vide = tous les produits du catalogue.
              </p>
            </div>
          )}

          {/* Name (with live suggestion) */}
          <div>
            <label className="text-xs text-muted dark:text-muted-dark block mb-1">
              Nom du panneau
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={suggestedName || "Mon panneau"}
              className="bun-textbox w-full px-3 py-2 text-sm rounded-lg border border-black/10 dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-ink/30 dark:focus:ring-white/30"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-full text-sm text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={
                pending ||
                !effectiveName ||
                (cfg.needsClient && !client.trim())
              }
              className="px-4 py-1.5 rounded-full bg-ink text-white text-sm font-semibold flex items-center gap-2 hover:bg-ink/90 disabled:opacity-50"
            >
              {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {isEdit ? "Enregistrer" : "Créer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
